import '@kitware/vtk.js/Rendering/Profiles/Geometry';
import '@kitware/vtk.js/Rendering/Profiles/Glyph';

import type vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkOrientationMarkerWidget from '@kitware/vtk.js/Interaction/Widgets/OrientationMarkerWidget';
import vtkXMLPolyDataReader from '@kitware/vtk.js/IO/XML/XMLPolyDataReader';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';

import type { IViewport } from './types';

function createOrientationMarkerWidget(
    interactor: vtkRenderWindowInteractor,
    parentRenderer: vtkRenderer,
    actor: vtkActor
): vtkOrientationMarkerWidget {
    const property = actor.getProperty();
    property.setColor(0.8, 0.5, 0.4);
    property.setOpacity(1.0);
    property.setSpecular(0.0);
    property.setSpecularPower(20);

    const orientationWidget = vtkOrientationMarkerWidget.newInstance({
        actor,
        interactor,
        viewportSize: 0.18,
        minPixelSize: 100,
        maxPixelSize: 300,
        parentRenderer,
    });

    orientationWidget.setEnabled(true);
    orientationWidget.setViewportCorner(vtkOrientationMarkerWidget.Corners.BOTTOM_RIGHT);

    return orientationWidget;
}

/**
 * OrientationWidget — attaches a VTK orientation marker to a viewport.
 *
 * The human-body.vtp model is loaded once (static cache) and reused across all
 * viewports. Concurrent attach() calls before the download completes share the
 * same in-flight Promise, so the file is fetched at most once per page load.
 */
export class OrientationWidget {
    private static readonly MODEL_PATH = 'human-body.vtp';

    private static polyDataCache: vtkPolyData | null = null;
    private static loadingPromise: Promise<vtkPolyData> | null = null;

    private readonly orientationMarkerWidget: vtkOrientationMarkerWidget;
    private readonly actor: vtkActor;
    private readonly mapper: vtkMapper;

    private _disposed = false;

    private constructor(
        orientationMarkerWidget: vtkOrientationMarkerWidget,
        actor: vtkActor,
        mapper: vtkMapper
    ) {
        this.orientationMarkerWidget = orientationMarkerWidget;
        this.actor = actor;
        this.mapper = mapper;
    }

    private static async loadPolyData(): Promise<vtkPolyData> {
        if (OrientationWidget.polyDataCache) {
            return OrientationWidget.polyDataCache;
        }

        if (OrientationWidget.loadingPromise) {
            return OrientationWidget.loadingPromise;
        }

        OrientationWidget.loadingPromise = (async () => {
            const url = `/${OrientationWidget.MODEL_PATH}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(
                    `OrientationWidget: failed to load model (${response.status} ${response.statusText}) at ${url}`
                );
            }

            const buffer = await response.arrayBuffer();

            const reader = vtkXMLPolyDataReader.newInstance();
            reader.parseAsArrayBuffer(buffer);

            const polyData = reader.getOutputData(0) as vtkPolyData;
            reader.delete();

            OrientationWidget.polyDataCache = polyData;
            return polyData;
        })().catch((error) => {
            // Reset promise so future calls can retry
            OrientationWidget.loadingPromise = null;
            throw error;
        });

        return OrientationWidget.loadingPromise;
    }

    /**
     * Attach the orientation widget to a viewport.
     *
     * @param viewport     Target viewport
     * @param isCancelled  Cancellation callback (e.g. () => this.disposed)
     * @param layer        Renderer layer index (default: 0)
     */
    static async attach(
        viewport: IViewport,
        isCancelled: () => boolean,
        layer = 0
    ): Promise<OrientationWidget | null> {
        const polyData = await OrientationWidget.loadPolyData();

        // Viewport might have been disposed while loading
        if (isCancelled()) {
            return null;
        }

        const mapper = vtkMapper.newInstance();
        mapper.setInputData(polyData);

        const actor = vtkActor.newInstance();
        actor.setMapper(mapper);
        const renderer = viewport.getRenderer(layer);
        const interactor = viewport.getRenderWindow().getInteractor();

        const orientationMarkerWidget = createOrientationMarkerWidget(interactor, renderer, actor);

        viewport.render();

        return new OrientationWidget(orientationMarkerWidget, actor, mapper);
    }

    /**
     * Remove this widget from the viewport and release all VTK resources.
     */
    dispose(): void {
        if (this._disposed) return;
        this._disposed = true;

        this.orientationMarkerWidget.setEnabled(false);
        this.orientationMarkerWidget.delete();

        this.actor.delete();
        this.mapper.delete();
    }
}
